import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, DollarSign, Users, ArrowUpFromLine, ArrowDownToLine, ArrowLeftRight, TrendingUp, Rocket, Flame, ExternalLink, MessageCircle, Heart, UserPlus, ShieldCheck, Clock, CheckCircle, XCircle, Coins } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { useNotifications } from '@/hooks/useNotifications';
import { formatTimeAgo } from '@/data/stats';
import { cn } from '@/lib/utils';
import { AppNotification } from '@/lib/notifications';
import { addBreadcrumb } from '@/lib/sentry';

const DROPDOWN_LIMIT = 5;

const getNotificationIcon = (type: AppNotification['type']) => {
  switch (type) {
    case 'creator_fees_claimable': return DollarSign;
    case 'creator_fees_claimed': return Coins;
    case 'withdrawal_completed': return ArrowUpFromLine;
    case 'deposit_received': return ArrowDownToLine;
    case 'trade_executed': return ArrowLeftRight;
    case 'swap_completed': return ArrowLeftRight;
    case 'token_watchlist_movement': return TrendingUp;
    case 'followed_creator_launch': return Rocket;
    case 'own_token_launch': return Rocket;
    case 'watchlisted_token_trending': return Flame;
    case 'new_comment_on_token': return MessageCircle;
    case 'new_like_on_token': return Heart;
    case 'new_follower': return UserPlus;
    // Verification notifications
    case 'verification_submitted': return Clock;
    case 'verification_under_review': return ShieldCheck;
    case 'verification_approved': return CheckCircle;
    case 'verification_rejected': return XCircle;
    default: return Bell;
  }
};

const getIconStyles = (type: AppNotification['type']) => {
  switch (type) {
    case 'creator_fees_claimable':
    case 'creator_fees_claimed':
    case 'withdrawal_completed':
    case 'deposit_received':
    case 'trade_executed':
    case 'swap_completed':
      return 'bg-success/20 text-success';
    case 'followed_creator_launch':
      return 'bg-blue-500/20 text-blue-500';
    case 'own_token_launch':
      return 'bg-primary/20 text-primary';
    case 'new_comment_on_token':
      return 'bg-blue-500/20 text-blue-500';
    case 'new_like_on_token':
      return 'bg-red-500/20 text-red-500';
    case 'new_follower':
      return 'bg-purple-500/20 text-purple-500';
    // Verification notification styles
    case 'verification_submitted':
      return 'bg-yellow-500/20 text-yellow-500';
    case 'verification_under_review':
      return 'bg-blue-500/20 text-blue-500';
    case 'verification_approved':
      return 'bg-green-500/20 text-green-500';
    case 'verification_rejected':
      return 'bg-red-500/20 text-red-500';
    default:
      return 'bg-secondary text-muted-foreground';
  }
};

export function NotificationDropdown() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { notifications, unreadCount, markRead, markAllRead, clearAll } = useNotifications();
  
  const displayNotifications = [...notifications]
    .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
    .slice(0, DROPDOWN_LIMIT);

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen) {
      addBreadcrumb('notification.dropdown.opened', 'ui');
    }
  };

  const handleItemClick = (notification: AppNotification) => {
    markRead(notification.id);
    addBreadcrumb('notification.dropdown.item_clicked', 'ui', { type: notification.type });
    
    if (notification.cta.path.startsWith('http')) {
      window.open(notification.cta.path, '_blank', 'noopener,noreferrer');
    } else {
      navigate(notification.cta.path);
    }
    setOpen(false);
  };

  const handleViewAll = () => {
    navigate('/notifications');
    setOpen(false);
  };

  const handleMarkAllRead = () => {
    markAllRead();
  };

  const handleClearAll = () => {
    clearAll();
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="relative h-11 w-11 md:h-9 md:w-9"
          aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 size-2 animate-bounce rounded-full bg-red-500 dark:bg-red-400" />
          )}
          <span className="sr-only">Notifications</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        align="end" 
        className="w-[calc(100vw-32px)] sm:w-[360px] max-w-[360px] p-0 bg-popover border-border z-50"
        sideOffset={8}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="font-semibold text-sm">Notifications</h3>
          <div className="flex items-center gap-3">
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Mark all as read
              </button>
            )}
            {displayNotifications.length > 0 && (
              <button
                onClick={handleClearAll}
                className="text-xs text-muted-foreground hover:text-destructive transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Notification List */}
        {displayNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 px-4">
            <div className="w-14 h-14 rounded-full bg-[#CCFF00]/10 flex items-center justify-center mb-3">
              <Bell className="h-7 w-7 text-[#CCFF00]" />
            </div>
            <p className="text-sm text-muted-foreground">No notifications yet</p>
          </div>
        ) : (
          <div className="max-h-[300px] overflow-y-auto">
            {displayNotifications.map((notification) => {
              const Icon = getNotificationIcon(notification.type);
              return (
                <div
                  key={notification.id}
                  onClick={() => handleItemClick(notification)}
                  className="flex items-start gap-3 px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors"
                >
                  {/* Icon or Token Image */}
                  {notification.tokenImage ? (
                    <img
                      loading="lazy"
                      src={notification.tokenImage}
                      alt={notification.tokenName || 'Coin'}
                      className="w-8 h-8 rounded-full flex-shrink-0 object-cover"
                    />
                  ) : (
                    <div className={cn(
                      'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
                      getIconStyles(notification.type)
                    )}>
                      <Icon className="w-4 h-4" />
                    </div>
                  )}

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium line-clamp-1">{notification.title}</p>
                    <p className="text-xs text-muted-foreground line-clamp-1">{notification.message}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatTimeAgo(notification.timestamp)}
                    </p>
                  </div>

                  {/* Unread Indicator */}
                  {!notification.read && (
                    <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-2" />
                  )}
                  
                  {/* External Link Indicator */}
                  {notification.cta.path.startsWith('http') && (
                    <ExternalLink className="w-3 h-3 text-muted-foreground flex-shrink-0 mt-2" />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div className="border-t border-border">
          <button
            onClick={handleViewAll}
            className="w-full py-3 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
          >
            View all notifications →
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
